/**
 * AbsenceForm — modal to register one or several absences (vacations,
 * personal day, sick leave, permission, other) for one or several employees
 * over a date range, with a chosen hour pattern (full day / morning / after-
 * noon / custom hours).
 *
 * Always create-mode in this iteration: editing an existing absence is done
 * via approve/reject/delete actions in the pending list. To "edit" a single
 * day in practice the admin deletes and recreates — much simpler than a per-
 * day editor and covers all reasonable cases.
 */
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { plannerApi, type PlannerResource } from '@/services/api';
import { LoadingSpinner } from '@/components/common';

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';

type AbsenceKind = 'vacations' | 'personal' | 'sick' | 'permission' | 'other';
type Pattern = 'full' | 'morning' | 'afternoon' | 'custom';
type Status = 'approved' | 'pending';

const ABSENCE_LABEL: Record<AbsenceKind, string> = {
  vacations: 'Vacaciones',
  personal: 'Asuntos propios',
  sick: 'Baja médica',
  permission: 'Permiso retribuido',
  other: 'Otros',
};

const PATTERN_LABEL: Record<Pattern, string> = {
  full: 'Día completo',
  morning: 'Media jornada (mañana 09:00–14:00)',
  afternoon: 'Media jornada (tarde 15:00–20:00)',
  custom: 'Horas concretas',
};

function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function AbsenceForm({
  employees, defaultResourceId, defaultDate, onClose, onSaved,
}: {
  employees: PlannerResource[]; // resources with employeeNif
  defaultResourceId?: string;   // when opened from a specific employee
  defaultDate?: Date;           // when opened from a specific cell
  onClose: () => void;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(() =>
    defaultResourceId ? [defaultResourceId] : []
  );
  const initialDate = defaultDate || new Date();
  const [dateFrom, setDateFrom] = useState(ymdLocal(initialDate));
  const [dateTo, setDateTo] = useState(ymdLocal(initialDate));
  const [absenceKind, setAbsenceKind] = useState<AbsenceKind>('vacations');
  const [pattern, setPattern] = useState<Pattern>('full');
  const [hourStart, setHourStart] = useState('09:00');
  const [hourEnd, setHourEnd] = useState('14:00');
  const [status, setStatus] = useState<Status>('approved');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const dayCount = useMemo(() => {
    const a = new Date(dateFrom);
    const b = new Date(dateTo);
    if (isNaN(a.getTime()) || isNaN(b.getTime()) || +b < +a) return 0;
    return Math.floor((+b - +a) / 86400000) + 1;
  }, [dateFrom, dateTo]);
  const totalItems = selected.length * dayCount;

  const toggleEmployee = (id: string) => {
    setSelected((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));
  };

  const save = async () => {
    if (selected.length === 0) { toast.error('Selecciona al menos un empleado'); return; }
    if (!dateFrom || !dateTo) { toast.error('Fechas obligatorias'); return; }
    if (new Date(dateTo) < new Date(dateFrom)) { toast.error('La fecha "hasta" debe ser igual o posterior a "desde"'); return; }
    if (pattern === 'custom' && (!hourStart || !hourEnd || hourStart >= hourEnd)) {
      toast.error('Horas concretas inválidas (inicio < fin)');
      return;
    }
    setSaving(true);
    try {
      const r = await plannerApi.createAbsences({
        resourceIds: selected,
        dateFrom, dateTo,
        absenceKind,
        status,
        pattern,
        ...(pattern === 'custom' ? { hourStart, hourEnd } : {}),
        notes: notes.trim() || undefined,
      });
      const { inserted = 0 } = r.data || {};
      toast.success(`${inserted} ausencia${inserted === 1 ? '' : 's'} ${status === 'approved' ? 'aprobada(s)' : 'pendiente(s)'}`);
      onSaved();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Nueva ausencia</h2>
            <p className="text-sm text-gray-500">Vacaciones, asuntos propios, baja, permisos…</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Empleados */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Empleado(s)</label>
            {employees.length === 0 ? (
              <p className="text-sm text-amber-700">No hay recursos con NIF. Asigna un NIF en la pestaña Recursos antes de crear ausencias.</p>
            ) : (
              <div className="max-h-40 overflow-auto border border-gray-200 rounded-lg p-2 space-y-1">
                {employees.map((e) => (
                  <label key={e._id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
                    <input type="checkbox" checked={selected.includes(e._id)} onChange={() => toggleEmployee(e._id)} />
                    <span className="flex-1">{e.name}</span>
                    <span className="text-xs text-gray-400">NIF {e.employeeNif}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de ausencia</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(ABSENCE_LABEL) as AbsenceKind[]).map((k) => (
                <button key={k} type="button" onClick={() => setAbsenceKind(k)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${absenceKind === k ? 'border-vida-600 bg-vida-50 text-vida-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  {ABSENCE_LABEL[k]}
                </button>
              ))}
            </div>
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
              <input type="date" className={inputClass} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
              <input type="date" className={inputClass} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>

          {/* Patron horario */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Patrón horario por día</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(PATTERN_LABEL) as Pattern[]).map((p) => (
                <button key={p} type="button" onClick={() => setPattern(p)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${pattern === p ? 'border-vida-600 bg-vida-50 text-vida-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  {PATTERN_LABEL[p]}
                </button>
              ))}
            </div>
            {pattern === 'custom' && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Hora inicio</label>
                  <input type="time" className={inputClass} value={hourStart} onChange={(e) => setHourStart(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Hora fin</label>
                  <input type="time" className={inputClass} value={hourEnd} onChange={(e) => setHourEnd(e.target.value)} />
                </div>
              </div>
            )}
          </div>

          {/* Estado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setStatus('approved')}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm ${status === 'approved' ? 'border-green-600 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                Aprobada
              </button>
              <button type="button" onClick={() => setStatus('pending')}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm ${status === 'pending' ? 'border-amber-600 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                Pendiente
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              "Aprobada" cuenta como ausencia justificada en el calendario. "Pendiente" queda para que tú apruebes o rechaces más tarde.
            </p>
          </div>

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
            <textarea rows={2} className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {/* Resumen */}
          {totalItems > 0 && (
            <p className="text-xs text-gray-500">
              Se crearán <strong>{totalItems}</strong> registro{totalItems === 1 ? '' : 's'} ({selected.length} empleado{selected.length === 1 ? '' : 's'} × {dayCount} día{dayCount === 1 ? '' : 's'}).
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancelar</button>
          <button onClick={save} disabled={saving || selected.length === 0 || dayCount === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50">
            {saving && <LoadingSpinner size="sm" />} Crear ausencia{totalItems === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}
